import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
} from '@chakra-ui/react'
import React, { useEffect, useState, VFC } from 'react'
import { useTitle } from 'react-use'
import { Container } from '../../../common/components/Container'
import { Title } from '../../manager/common/atoms/title/Title'

type User = { id?: number; account: string; name: string }
type OfficeUser = { user: User; office?: { name: string } }
type LoginType = 'admin' | 'approver' | 'office'
type Option = { value: string; label: string }

const labels: Record<LoginType, string> = {
  admin: '管理者',
  approver: '承認者',
  office: '研究科',
}

async function request(path: string, init?: RequestInit) {
  const response = await fetch(`/debug-login/${path}`, { credentials: 'same-origin', ...init })
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
  return response
}

const DebugLogin: VFC<{ type: LoginType }> = ({ type }) => {
  useTitle(`${labels[type]} デバッグログイン`)
  const [options, setOptions] = useState<Option[]>([])
  const [selected, setSelected] = useState('')
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    request(`list/${type}`)
      .then((response) => response.json())
      .then((data: User[] | OfficeUser[]) => {
        const values = type === 'office'
          ? (data as OfficeUser[]).map(({ user, office }) => ({
              value: String(user.id ?? ''),
              label: `${office?.name ?? ''} ${user.name} (${user.account})`,
            }))
          : (data as User[]).map((user) => ({
              value: user.account,
              label: `${user.name} (${user.account})`,
            }))
        setOptions(values)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [type])

  const login = async () => {
    if (!selected) return
    const body = new URLSearchParams(type === 'office' ? { id: selected } : { account: selected })
    try {
      await request(type, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      })
      window.location.reload()
    } catch (_) {
      setError(true)
    }
  }

  return (
    <Container bgColor="gray" innerWidth="1280px" pb={16}>
      <Center minH="420px">
        <Box w="776px">
          <Stack spacing={6}>
            <Title text={`${labels[type]}としてログイン`} />
            <FormControl>
              <FormLabel>ログインするアカウント</FormLabel>
              <Select
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
                placeholder={loading ? '読み込み中...' : '選択してください'}
                isDisabled={loading}
              >
                {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            </FormControl>
            {error && <Alert status="error"><AlertIcon />アカウントの取得またはログインに失敗しました。</Alert>}
            <Button colorScheme="blue" isDisabled={!selected || loading} onClick={login}>ログイン</Button>
          </Stack>
        </Box>
      </Center>
    </Container>
  )
}

export const AdminDebugLogin: VFC = () => <DebugLogin type="admin" />
export const ApproverDebugLogin: VFC = () => <DebugLogin type="approver" />
export const OfficeDebugLogin: VFC = () => <DebugLogin type="office" />

export const ApplicantDebugLogin: VFC = () => {
  useTitle('申請者 デバッグログイン')
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)
  const login = async () => {
    try {
      await request('applicant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }),
      })
      window.location.assign('/')
    } catch (_) {
      setError(true)
    }
  }
  return (
    <Container bgColor="gray" innerWidth="1280px" pb={16}>
      <Center minH="420px"><Box w="776px"><Stack spacing={6}>
        <Title text="申請者としてログイン" />
        <FormControl><FormLabel>メールアドレス</FormLabel><Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></FormControl>
        {error && <Alert status="error"><AlertIcon />ログインに失敗しました。</Alert>}
        <Button colorScheme="blue" isDisabled={!email} onClick={login}>ログイン</Button>
      </Stack></Box></Center>
    </Container>
  )
}
